import React, { useState, useEffect } from 'react';
import { Plus, MessageSquare, MoreHorizontal, Trash2, Edit2, Check, X, ChevronLeft } from 'lucide-react';
import axios from 'axios';
import { auth } from '../../utils/auth';
import { formatDistanceToNow } from 'date-fns';
import { nb } from 'date-fns/locale';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ThreadSidebar = ({ 
  caseId, 
  activeThreadId, 
  onSelectThread, 
  onNewThread,
  isCollapsed = false,
  onToggleCollapse
}) => {
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [menuOpenId, setMenuOpenId] = useState(null);

  useEffect(() => {
    if (caseId) {
      fetchThreads();
    }
  }, [caseId]);

  const fetchThreads = async () => {
    try {
      const token = auth.getToken();
      // Add cache-busting timestamp to prevent CDN caching
      const cacheBuster = Date.now();
      const response = await axios.get(`${API}/chat/threads?case_id=${caseId}&_t=${cacheBuster}`, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        }
      });
      setThreads(response.data.threads || []);
    } catch (error) {
      console.error('Failed to fetch threads:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateThread = async () => {
    try {
      const token = auth.getToken();
      const response = await axios.post(`${API}/chat/threads`, 
        { case_id: caseId, title: 'Ny chat' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const newThread = response.data;
      setThreads(prev => [newThread, ...prev]);
      onSelectThread(newThread.id);
      if (onNewThread) onNewThread(newThread);
    } catch (error) {
      console.error('Failed to create thread:', error);
    }
  };

  const handleUpdateTitle = async (threadId) => {
    if (!editTitle.trim()) {
      setEditingId(null);
      return;
    }
    try {
      const token = auth.getToken();
      await axios.patch(`${API}/chat/threads/${threadId}?title=${encodeURIComponent(editTitle)}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setThreads(prev => prev.map(t => 
        t.id === threadId ? { ...t, title: editTitle } : t
      ));
      setEditingId(null);
    } catch (error) {
      console.error('Failed to update thread:', error);
    }
  };

  const handleDeleteThread = async (threadId) => {
    try {
      const token = auth.getToken();
      await axios.delete(`${API}/chat/threads/${threadId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setThreads(prev => prev.filter(t => t.id !== threadId));
      if (activeThreadId === threadId) {
        const remaining = threads.filter(t => t.id !== threadId);
        onSelectThread(remaining[0]?.id || null);
      }
      setMenuOpenId(null);
    } catch (error) {
      console.error('Failed to delete thread:', error);
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    try {
      return formatDistanceToNow(new Date(dateStr), { addSuffix: true, locale: nb });
    } catch {
      return '';
    }
  };

  if (isCollapsed) {
    return (
      <div className="w-12 bg-gray-50 border-r border-gray-200 flex flex-col items-center py-3 gap-2 flex-shrink-0">
        <button
          onClick={onToggleCollapse}
          className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
          title="Vis samtaler"
        >
          <MessageSquare className="w-5 h-5 text-gray-600" />
        </button>
        <button
          onClick={handleCreateThread}
          className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
          title="Ny chat"
        >
          <Plus className="w-5 h-5 text-gray-600" />
        </button>
      </div>
    );
  }

  return (
    <div className="w-52 bg-gray-50 border-r border-gray-200 flex flex-col flex-shrink-0">
      {/* Header */}
      <div className="p-2 border-b border-gray-200">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold text-sm text-gray-900">Samtaler</h3>
          <button
            onClick={onToggleCollapse}
            className="p-1 hover:bg-gray-200 rounded transition-colors"
          >
            <ChevronLeft className="w-4 h-4 text-gray-500" />
          </button>
        </div>
        <button
          onClick={handleCreateThread}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-gray-900 hover:bg-gray-800 text-white rounded-lg text-sm font-medium transition-colors"
        >
          <Plus className="w-4 h-4" />
          Ny chat
        </button>
      </div>

      {/* Thread List */}
      <div className="flex-1 overflow-y-auto p-2">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="w-5 h-5 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
          </div>
        ) : threads.length === 0 ? (
          <div className="text-center py-8 px-4">
            <MessageSquare className="w-8 h-8 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500">Ingen samtaler ennå</p>
            <p className="text-xs text-gray-400 mt-1">Start en ny chat for å begynne</p>
          </div>
        ) : (
          <div className="space-y-1">
            {threads.map(thread => (
              <div
                key={thread.id}
                className={`group relative rounded-lg transition-colors ${
                  activeThreadId === thread.id
                    ? 'bg-gray-200'
                    : 'hover:bg-gray-100'
                }`}
              >
                {editingId === thread.id ? (
                  <div className="p-2">
                    <input
                      type="text"
                      value={editTitle}
                      onChange={(e) => setEditTitle(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleUpdateTitle(thread.id);
                        if (e.key === 'Escape') setEditingId(null);
                      }}
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-gray-900/20"
                      autoFocus
                    />
                    <div className="flex gap-1 mt-1">
                      <button
                        onClick={() => handleUpdateTitle(thread.id)}
                        className="p-1 hover:bg-gray-200 rounded"
                      >
                        <Check className="w-3.5 h-3.5 text-green-600" />
                      </button>
                      <button
                        onClick={() => setEditingId(null)}
                        className="p-1 hover:bg-gray-200 rounded"
                      >
                        <X className="w-3.5 h-3.5 text-gray-500" />
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => onSelectThread(thread.id)}
                    className="w-full text-left p-2"
                  >
                    <div className="flex items-start gap-2">
                      <MessageSquare className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {thread.title || 'Ny chat'}
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {thread.message_count || 0} meldinger • {formatTime(thread.last_message_at || thread.created_at)}
                        </p>
                      </div>
                    </div>
                  </button>
                )}

                {/* Menu button */}
                {editingId !== thread.id && (
                  <div className="absolute right-1 top-1">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setMenuOpenId(menuOpenId === thread.id ? null : thread.id);
                      }}
                      className={`p-1 rounded hover:bg-gray-200 transition-opacity ${
                        menuOpenId === thread.id || activeThreadId === thread.id
                          ? 'opacity-100'
                          : 'opacity-0 group-hover:opacity-100'
                      }`}
                    >
                      <MoreHorizontal className="w-4 h-4 text-gray-500" />
                    </button>

                    {menuOpenId === thread.id && (
                      <>
                        <div className="fixed inset-0 z-10" onClick={() => setMenuOpenId(null)} />
                        <div className="absolute right-0 top-6 w-36 bg-white border border-gray-200 rounded-lg shadow-lg z-20 py-1">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditTitle(thread.title || '');
                              setEditingId(thread.id);
                              setMenuOpenId(null);
                            }}
                            className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                          >
                            <Edit2 className="w-3.5 h-3.5" />
                            Endre navn
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteThread(thread.id);
                            }}
                            className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                            Slett
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ThreadSidebar;
